import React, { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { ArrowLeftRightIcon, GiftIcon, HistoryIcon, Undo2Icon } from 'lucide-react'

import { Box, Link, Stack, Typography } from '@mui/material'

import { ActionDropdown } from 'components/input/ActionDropdown/ActionDropdown'
import { EmptyState } from 'components/display/EmptyState/EmptyState'
import { StatusChip } from 'components/display/StatusChip/StatusChip'
import { Table } from 'components/display/Table/Table'
import { REFERRAL_STAFF_STATUSES } from 'constants/statuses'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { formatCount } from 'constants/referrals'
import { useCanAccess, useReferrerState } from 'hooks'
import type { StaffReferral } from 'hooks/api/useInternalReferrals'
import { ROUTES } from 'routes'
import { dateOnly } from 'utils'

import { CHANNEL_LABELS, INELIGIBLE_REASON_LABELS, prospectOf, referredByOf } from './referralLabels'
import { ReattributeReferralModal, ReferralHistoryModal, ReverseCreditsModal } from './ReferralActionModals'

const cellSx = { height: '100%', minWidth: 0, justifyContent: 'center' }

const STATUS_OPTIONS = statusFilterOptions(REFERRAL_STAFF_STATUSES)

const PLATFORM_ADMIN_ONLY = 'Only platform admins can change a referral.'

/**
 * VRG-658 — the staff referrals table, ONE definition rendered by both the Signups →
 * Referrals tab and the internal company page's Referrals tab (working-conventions §71: a
 * detail tab renders the same table as its list, scoped, never a second column list).
 *
 * `scope="company"` drops the "Referred by" column, which on a company's own sent-referrals
 * list would say the company's own name on every row.
 *
 * Row actions are PLATFORM ADMIN only (Spencer's default). For a platform manager they show
 * DISABLED with a tooltip naming the requirement (§27/§46: permission disables, never hides).
 * Record state HIDES: "Reverse credits" only appears on a referral that has credits, and
 * "Re-attribute" only on a brand referral (an Insider referral is attributed in FirstPromoter).
 */
export const StaffReferralsTable = ({
  rows,
  loading,
  scope = 'all',
  height,
  fillBottom,
  emptyTitle = 'No referrals yet',
  emptyText = 'When a brand refers another brand, or an Insider sends a lead, it shows up here.',
}: {
  rows: StaffReferral[]
  loading?: boolean
  scope?: 'all' | 'company'
  height?: number
  fillBottom?: boolean
  emptyTitle?: string
  emptyText?: string
}) => {
  const { isHasAccess: isPlatformAdmin } = useCanAccess({ role: 'platform_admin' })
  const referrer = useReferrerState()
  const [reversing, setReversing] = useState<StaffReferral | null>(null)
  const [reattributing, setReattributing] = useState<StaffReferral | null>(null)
  const [history, setHistory] = useState<StaffReferral | null>(null)

  const columns = useMemo(
    () => [
      {
        field: 'prospect',
        headerName: 'Prospect',
        type: 'string',
        flex: 1,
        minWidth: 220,
        sortable: false,
        valueGetter: (_: unknown, row: StaffReferral) => prospectOf(row).primary,
        renderCell: ({ row }: { row: StaffReferral }) => {
          const { primary, secondary } = prospectOf(row)
          return (
            <Stack sx={cellSx}>
              {row.referred_company ? (
                <Link
                  component={RouterLink}
                  to={ROUTES.INTERNAL_COMPANY(row.referred_company.id)}
                  state={referrer}
                  underline="hover"
                  color="inherit"
                  variant="body2"
                  noWrap
                >
                  {primary}
                </Link>
              ) : (
                <Typography variant="body2" noWrap>
                  {primary}
                </Typography>
              )}
              {secondary && (
                <Typography variant="body4" color="text.secondary" noWrap>
                  {secondary}
                </Typography>
              )}
            </Stack>
          )
        },
      },
      ...(scope === 'all'
        ? [
            {
              field: 'referred_by',
              headerName: 'Referred by',
              type: 'string',
              width: 220,
              sortable: false,
              valueGetter: (_: unknown, row: StaffReferral) => referredByOf(row)?.primary ?? '',
              renderCell: ({ row }: { row: StaffReferral }) => <ReferredByBlock referral={row} />,
            },
          ]
        : []),
      {
        field: 'channel',
        headerName: 'Channel',
        type: 'string',
        // §49: sized for the header plus its sort glyph ("Chan…" at 110).
        width: 124,
        valueGetter: (_: unknown, row: StaffReferral) => CHANNEL_LABELS[row.channel] ?? row.channel,
      },
      {
        field: 'status',
        headerName: 'Status',
        // Filterable from the table's own Filter drawer, as every status column is.
        type: 'statusChip',
        options: STATUS_OPTIONS,
        valueGetter: (_: unknown, row: StaffReferral) => row.status,
        width: 190,
        renderCell: ({ row }: { row: StaffReferral }) => (
          <Stack sx={cellSx} spacing={0.5}>
            <Box>
              <StatusChip statuses={REFERRAL_STAFF_STATUSES} status={row.status} size="small" />
            </Box>
            {row.ineligible_reason && (
              <Typography variant="body4" color="text.secondary" noWrap>
                {INELIGIBLE_REASON_LABELS[row.ineligible_reason] ?? row.ineligible_reason}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        field: 'credits',
        headerName: 'Credits / cap',
        type: 'number',
        width: 150,
        valueGetter: (_: unknown, row: StaffReferral) => row.credits.net,
        renderCell: ({ row }: { row: StaffReferral }) => (
          <Stack sx={cellSx}>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatCount(row.credits.net)} / {formatCount(row.credits.cap)}
            </Typography>
            <Typography variant="body4" color="text.secondary">
              {row.credits.percent_of_cap}% of cap
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'reversed',
        headerName: 'Reversed',
        type: 'number',
        // §49: "Revers…" at 120 with the sort glyph.
        width: 136,
        valueGetter: (_: unknown, row: StaffReferral) => row.credits.reversed,
        valueFormatter: (value: number) => (value ? formatCount(value) : '—'),
      },
      {
        field: 'first_paid_at',
        headerName: 'First paid',
        type: 'string',
        width: 130,
        valueFormatter: (value: string | null) => (value ? dateOnly(value) : '—'),
      },
      {
        field: 'created_at',
        headerName: 'Created',
        type: 'string',
        width: 130,
        valueFormatter: (value: string | null) => (value ? dateOnly(value) : '—'),
      },
      {
        field: 'actions',
        headerName: '',
        type: 'custom',
        width: 54,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        disableReorder: true,
        pinned: 'right',
        renderCell: ({ row }: { row: StaffReferral }) => {
          const isBrand = row.channel !== 'insider'
          const gate = isPlatformAdmin ? {} : { disabled: true, tooltip: PLATFORM_ADMIN_ONLY }
          return (
            <ActionDropdown
              items={[
                { label: 'History', icon: <HistoryIcon strokeWidth={1} />, onClick: () => setHistory(row) },
                {
                  label: 'Re-attribute',
                  icon: <ArrowLeftRightIcon strokeWidth={1} />,
                  visible: isBrand,
                  onClick: () => setReattributing(row),
                  ...gate,
                },
                {
                  label: 'Reverse credits',
                  icon: <Undo2Icon strokeWidth={1} />,
                  visible: isBrand && row.credits.net > 0,
                  destructive: true,
                  divider: true,
                  onClick: () => setReversing(row),
                  ...gate,
                },
              ]}
            />
          )
        },
      },
    ],
    [scope, isPlatformAdmin, referrer],
  )

  return (
    <>
      <Table
        loading={loading}
        columns={columns}
        rows={rows}
        getRowId={(row: StaffReferral) => row.id}
        alwaysShowRowSelection={false}
        showSelection={false}
        height={height}
        fillBottom={fillBottom}
        paginationMode="client"
        initialPageSize={100}
        emptyState={<EmptyState title={emptyTitle} text={emptyText} icon={<GiftIcon strokeWidth={1} size={32} />} />}
      />
      <ReverseCreditsModal referral={reversing} onClose={() => setReversing(null)} />
      <ReattributeReferralModal referral={reattributing} onClose={() => setReattributing(null)} />
      <ReferralHistoryModal referral={history} onClose={() => setHistory(null)} />
    </>
  )
}

/** "Referred by": a brand (company, then the member who sent it), an Insider, or nothing. */
export const ReferredByBlock = ({
  referral,
}: {
  referral: Parameters<typeof referredByOf>[0]
}) => {
  const by = referredByOf(referral)
  if (!by) return null
  return (
    <Stack sx={cellSx}>
      <Typography variant="body2" noWrap>
        {by.primary}
      </Typography>
      {by.secondary && (
        <Typography variant="body4" color="text.secondary" noWrap>
          {by.secondary}
        </Typography>
      )}
    </Stack>
  )
}
